import { useSyncExternalStore } from 'react'
import { isNativePlatform } from '@/lib/platform'
import {
  getSupportSessionInfo,
  getSupportSessionStatus,
  subscribeSupportSession,
  type SupportSessionInfo,
} from '@/lib/supportSession'

export interface SupportSessionView {
  /** True while this browser tab works in a live FlowRent support session. */
  active: boolean
  /** What GET /api/session (or the exchange) said about the session. */
  info: SupportSessionInfo | null
  scope: SupportSessionInfo['scope'] | null
}

/**
 * EP-160 section 2.3: whether this tab is a FlowRent support session, for the
 * banner and for hiding what the deny list refuses. Always inactive on the
 * operator app, which never runs a session.
 */
export function useSupportSession(): SupportSessionView {
  const status = useSyncExternalStore(subscribeSupportSession, getSupportSessionStatus)
  const info = useSyncExternalStore(subscribeSupportSession, getSupportSessionInfo)
  const active = status === 'active' && !isNativePlatform()
  return { active, info: active ? info : null, scope: active ? (info?.scope ?? null) : null }
}

/**
 * EP-160 slice 2: true only in a support session with scope `setup` ("Set up
 * for me"). That scope may not create tenant data: the API refuses every
 * action in `SupportSession::Blocked::TENANT_CREATES` (a customer, a shared
 * owner, a visitor visit, a lease and its renewal, a booking, an owner
 * transfer) with 422 `scope_setup_no_tenants`. Entry points to those actions
 * read this and hide themselves rather than lead to a refusal.
 */
export function useSetupScopeSession(): boolean {
  const { active, scope } = useSupportSession()
  return active && scope === 'setup'
}
